import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, KeyRound, UserCog, AlertTriangle, CheckCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Emergency admin recovery page.
 *
 * Reachable at /admin/recovery. Gated by BOOTSTRAP_SECRET (server-side env
 * var). Two flows:
 *   1. "Promote me to admin" — flips the currently-logged-in user's role to
 *      `admin`. Requires you be logged in already (as any role).
 *   2. "Reset admin password" — re-hashes the password for an existing admin
 *      account. Useful if the first-deploy hash got mangled.
 *
 * When to use: production seeded an admin whose login now 500s or you lost
 * the password, and you can't touch the DB directly. Set BOOTSTRAP_SECRET
 * in your deployment env, redeploy, come to this page.
 */
export default function AdminRecovery() {
  const [tab, setTab] = useState('promote'); // 'promote' | 'reset'
  const [secret, setSecret] = useState('');
  const [resetEmail, setResetEmail] = useState('jon@freddymedia.com');
  const [resetPassword, setResetPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [me, setMe] = useState(null);
  const [checkingSession, setCheckingSession] = useState(true);

  // Check current session so the user sees whose account will be promoted
  useEffect(() => {
    const token = localStorage.getItem('propost_token');
    if (!token) { setCheckingSession(false); return; }
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setMe(r.data))
      .catch(() => setMe(null))
      .finally(() => setCheckingSession(false));
  }, []);

  const promoteSelf = async () => {
    setError(''); setSuccess(''); setLoading(true);
    try {
      const token = localStorage.getItem('propost_token');
      const { data } = await axios.post(
        `${API}/admin/bootstrap/promote-self`,
        { bootstrap_secret: secret },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSuccess(`✅ ${data.message} Reloading into /admin in 2s…`);
      // Full page reload (not client-side navigate) so the auth context
      // refetches /auth/me and picks up the new admin role.
      setTimeout(() => { window.location.href = '/admin'; }, 2000);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (e.message || 'Something went wrong'));
    } finally { setLoading(false); }
  };

  const resetAdminPassword = async () => {
    setError(''); setSuccess(''); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/bootstrap/reset-admin-password`, {
        bootstrap_secret: secret,
        email: resetEmail.trim().toLowerCase(),
        new_password: resetPassword,
      });
      setSuccess(`✅ ${data.message}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (e.message || 'Something went wrong'));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#090E33] flex items-center justify-center px-4 py-10" data-testid="admin-recovery">
      <div className="max-w-lg w-full">
        <div className="text-center mb-6">
          <Shield className="w-10 h-10 text-[#FF4343] mx-auto mb-3" />
          <h1 className="font-display text-2xl sm:text-3xl font-bold">Admin <span className="text-[#FF4343]">Recovery</span></h1>
          <p className="text-xs text-[#9CA3AF] mt-2">Escape hatch for founders. Gated by <code className="text-[#FF4343]">BOOTSTRAP_SECRET</code> — set it in your deployment env before this will work.</p>
        </div>

        <div className="bg-[#111827] border border-[#FF4343]/30 border-l-[3px] border-l-[#FF4343] p-3 mb-5">
          <div className="flex gap-2 items-start">
            <AlertTriangle className="w-4 h-4 text-[#FF4343] flex-shrink-0 mt-0.5" />
            <div className="text-xs text-[#9CA3AF]">
              <strong className="text-white">One-time use.</strong> After you regain admin access, remove <code className="text-[#FF4343]">BOOTSTRAP_SECRET</code> from your env and redeploy so this door closes behind you.
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10 mb-5">
          <button data-testid="recovery-tab-promote" onClick={() => { setTab('promote'); setError(''); setSuccess(''); }}
            className={`flex-1 pb-3 text-sm font-medium ${tab === 'promote' ? 'text-[#FF4343] border-b-2 border-[#FF4343]' : 'text-[#9CA3AF]'}`}>
            <UserCog className="w-3.5 h-3.5 inline mr-1.5" />Promote me
          </button>
          <button data-testid="recovery-tab-reset" onClick={() => { setTab('reset'); setError(''); setSuccess(''); }}
            className={`flex-1 pb-3 text-sm font-medium ${tab === 'reset' ? 'text-[#FF4343] border-b-2 border-[#FF4343]' : 'text-[#9CA3AF]'}`}>
            <KeyRound className="w-3.5 h-3.5 inline mr-1.5" />Reset admin password
          </button>
        </div>

        <div className="bg-[#111827] border border-white/10 p-5 sm:p-6 space-y-4">
          {/* Secret is common to both flows */}
          <div>
            <label className="text-[10px] font-mono uppercase text-[#6B7280] block mb-1">BOOTSTRAP_SECRET</label>
            <input type="password" value={secret} onChange={e => setSecret(e.target.value)}
              placeholder="Paste the value you set in your Emergent env"
              data-testid="recovery-secret-input"
              className="w-full px-3 py-2.5 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343] font-mono" />
          </div>

          {tab === 'promote' ? (
            <>
              <div className="bg-white/5 border border-white/10 p-3 text-xs">
                {checkingSession ? (
                  <p className="text-[#9CA3AF]">Checking your session…</p>
                ) : me ? (
                  <>
                    <p className="text-[10px] font-mono uppercase text-[#6B7280]">This will promote:</p>
                    <p className="text-white font-semibold mt-0.5" data-testid="recovery-current-user">{me.name || 'Your account'} &lt;{me.email}&gt;</p>
                    <p className="text-[10px] text-[#9CA3AF] mt-0.5">Current role: <span className={me.role === 'admin' ? 'text-[#10B981]' : 'text-[#FF4343]'}>{me.role}</span></p>
                  </>
                ) : (
                  <p className="text-[#FF4343]">Not logged in. Log in as any user first, then come back here.</p>
                )}
              </div>
              <button onClick={promoteSelf} disabled={loading || !secret || !me}
                data-testid="recovery-promote-btn"
                className="w-full border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] py-3 text-sm font-semibold hover:bg-[#E03030] disabled:opacity-50">
                {loading ? 'Promoting…' : 'Promote me to admin'}
              </button>
            </>
          ) : (
            <>
              <div>
                <label className="text-[10px] font-mono uppercase text-[#6B7280] block mb-1">Admin email to reset</label>
                <input type="email" value={resetEmail} onChange={e => setResetEmail(e.target.value)}
                  data-testid="recovery-reset-email"
                  className="w-full px-3 py-2.5 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
                <p className="text-[10px] text-[#6B7280] mt-1">Must already have <code>role: admin</code>. This won&apos;t create a new admin.</p>
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase text-[#6B7280] block mb-1">New password (min 8 chars)</label>
                <input type="password" value={resetPassword} onChange={e => setResetPassword(e.target.value)}
                  data-testid="recovery-reset-password"
                  className="w-full px-3 py-2.5 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
              </div>
              <button onClick={resetAdminPassword} disabled={loading || !secret || !resetEmail || resetPassword.length < 8}
                data-testid="recovery-reset-btn"
                className="w-full border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] py-3 text-sm font-semibold hover:bg-[#E03030] disabled:opacity-50">
                {loading ? 'Resetting…' : 'Reset admin password'}
              </button>
            </>
          )}

          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 text-red-400 text-xs" data-testid="recovery-error">
              {error}
            </div>
          )}
          {success && (
            <div className="p-3 bg-[#10B981]/10 border border-[#10B981]/20 text-[#10B981] text-xs flex items-start gap-2" data-testid="recovery-success">
              <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{success}</span>
            </div>
          )}
        </div>

        <p className="text-center text-[10px] text-[#6B7280] mt-5">
          Not sure what this page is? It&apos;s not for you.{' '}
          <a href="/" className="text-[#FF4343] hover:underline">Go home</a>
        </p>
      </div>
    </div>
  );
}
